import React from 'react';
import { Button, Grid } from '@material-ui/core';
import {
  Card,
  CardHeader,
  CardContent,
  Typography,
  Box,
} from '@material-ui/core';
import {
  EntityApiDefinitionCard,
  EntityConsumedApisCard,
  EntityConsumingComponentsCard,
  EntityHasApisCard,
  EntityProvidedApisCard,
  EntityProvidingComponentsCard,
} from '@backstage/plugin-api-docs';
import {
  TemplateUsageSummaryCard,
  TemplateMonthlyStatsCard,
  TemplateTaskRunsCard,
} from '@codeverse-gp/plugin-usage-statistics';
import {
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityHasComponentsCard,
  EntityHasResourcesCard,
  EntityHasSubcomponentsCard,
  EntityHasSystemsCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  EntityOrphanWarning,
  EntityProcessingErrorsPanel,
  isComponentType,
  isResourceType,
  isKind,
  hasCatalogProcessingErrors,
  isOrphan,
  hasRelationWarnings,
  EntityRelationWarning,
} from '@backstage/plugin-catalog';
import {
  EntityUserProfileCard,
  EntityGroupProfileCard,
  EntityMembersListCard,
  EntityOwnershipCard,
} from '@backstage/plugin-org';
import { EntityTechdocsContent } from '@backstage/plugin-techdocs';
import { EmptyState } from '@backstage/core-components';
import {
  Direction,
  EntityCatalogGraphCard,
} from '@backstage/plugin-catalog-graph';
import {
  RELATION_API_CONSUMED_BY,
  RELATION_API_PROVIDED_BY,
  RELATION_CONSUMES_API,
  RELATION_DEPENDENCY_OF,
  RELATION_DEPENDS_ON,
  RELATION_HAS_PART,
  RELATION_PART_OF,
  RELATION_PROVIDES_API,
} from '@backstage/catalog-model';

import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';

import {
  EntityKubernetesContent,
  isKubernetesAvailable,
} from '@backstage/plugin-kubernetes';

import { NestleAboutCardPage } from '@internal/plugin-react-nestle-about-card';
import { StorageAccountContent } from '@internal/plugin-storage-account';
import { ManagementConfigsContent } from '@internal/plugin-management-configs';
import { isQuayAvailable, QuayPage } from '@backstage-community/plugin-quay';
import { EntityGithubSecretsContent } from '@internal/plugin-react-nestle-github-secrets';
import { EntityFactoryComponentsContent } from '@internal/plugin-react-nestle-factory-components';

import {
  EntityGithubActionsContent,
  isGithubActionsAvailable,
} from '@backstage-community/plugin-github-actions';
import { EntitySonarQubeCard } from '@backstage-community/plugin-sonarqube';
import { isSonarQubeAvailable } from '@backstage-community/plugin-sonarqube-react';

import { SonarQubeRelatedEntitiesOverview } from '@backstage-community/plugin-sonarqube';

import {
  EntityOverviewContent,
  EntityAnsibleContent,
} from '@internal/plugin-react-ansible-rhaap';

import {
  EntityGithubPullRequestsContent,
  //isGithubPullRequestsAvailable,
} from '@roadiehq/backstage-plugin-github-pull-requests';

import { Entity } from '@backstage/catalog-model';

import {
  EntityGithubCodespacesCard,
  isGithubCodespacesAvailable,
} from '@adityasinghal26/plugin-github-codespaces';

import { 
  EntityAzureSitesOverviewWidget, 
  // isAzureWebSiteNameAvailable 
} from '@backstage-community/plugin-azure-sites';

/**
 * Check if an entity has GitHub Secrets Management enabled.
 * Entities must have the nestle.com/enable-github-secrets-management annotation set to 'true'.
 *
 * @param entity - The Backstage entity to check
 * @returns true if the entity has secrets management enabled
 *
 * @example
 * ```yaml
 * metadata:
 *   annotations:
 *     nestle.com/enable-github-secrets-management: 'true'
 * ```
 */
/**
 * Check if an entity has GitHub Secrets Management enabled.
 * Entities must have the nestle.com/enable-github-secrets-management annotation set to 'true'.
 *
 * @param entity - The Backstage entity to check
 * @returns true if the entity has secrets management enabled
 *
 * @example
 * ```yaml
 * metadata:
 *   annotations:
 *     nestle.com/enable-github-secrets-management: 'true'
 * ```
 */
const isSecretsManagementEntity = (entity: Entity) =>
  entity.metadata.annotations?.['nestle.com/enable-github-secrets-management'] === 'true';

/**
 * Check if an entity is a Nexus Factory System.
 * Factory Systems have the nestle.com/factory-type annotation set to 'system'.
 * This enables the Nexus tab for managing installable Factory Applications (Components).
 *
 * @param entity - The Backstage entity to check
 * @returns true if the entity is a Nexus Factory System
 *
 * @example
 * ```yaml
 * metadata:
 *   annotations:
 *     nestle.com/factory-type: 'system'
 * ```
 */
const isFactorySystem = (entity: Entity) =>
  entity.metadata.annotations?.['nestle.com/factory-type'] === 'system';

/**
 * Check if an entity is a Regional System.
 * Regional Systems have the 'system-type' label set to 'regional'.
 * This enables the Onboarding card and other region-specific features.
 *
 * @param entity - The Backstage entity to check
 * @returns true if the entity is a Regional System
 *
 * @example
 * ```yaml
 * metadata:
 *   labels:
 *     system-type: 'regional'
 * ```
 */
const isRegionalSystem = (entity: Entity) =>
  entity.metadata.labels?.['system-type'] === 'regional';

// ============================================================================
// CUSTOM ENTITY KINDS SYSTEM
// ============================================================================
/**
 * This file implements a modular, extensible system for creating custom
 * entity kinds in Backstage.
 *
 * ## Quick Start: Adding a New Custom Entity Kind
 *
 * 1. Define your entity page in the "CUSTOM ENTITY PAGES" section:
 *    ```typescript
 *    const myEntityPage = createEntityPage({
 *      tabs: ['overview', 'cicd', 'kubernetes', 'docs']
 *    });
 *    ```
 *
 * 2. Register it in the main router:
 *    ```typescript
 *    <EntitySwitch.Case if={isKind('my-entity')} children={myEntityPage} />
 *    ```
 *
 * 3. Update catalog config to allow the kind:
 *    ```yaml
 *    rules:
 *      - allow: [Domain, System, Group, MyEntity]
 *    ```
 *
 * 4. Create entities with your new kind in YAML files
 *
 * For detailed documentation, see: docs/custom-entity-kinds.md
 */

// ============================================================================
// ANNOTATION CHECKERS
// ============================================================================
/**
 * Check if an entity has Ansible integration enabled.
 * Entities with the annotation 'nestle.com/enable-ansible: true' will show the Ansible tab.
 */
const isAnsibleEntity = (entity: Entity) =>
  entity.metadata.annotations?.['nestle.com/enable-ansible'] === 'true';

// ============================================================================
// REUSABLE TAB CONTENT COMPONENTS
// ============================================================================
// These are modular, reusable content blocks that can be composed into
// any entity page. This makes it easy to create custom entity kinds with
// specific tab configurations.

const techdocsContent = (
  <EntityTechdocsContent>
    <TechDocsAddons>
      <ReportIssue />
      <Mermaid
        config={{ theme: 'forest', themeVariables: { lineColor: '#000000' } }}
      />
    </TechDocsAddons>
  </EntityTechdocsContent>
);

const pullRequestsContent = (
  <EntityGithubPullRequestsContent />
);

const cicdContent = (
  // This is an example of how you can implement your company's logic in entity page.
  // You can for example enforce that all components of type 'service' should use GitHubActions
  <EntitySwitch>

    <EntitySwitch.Case if={isGithubActionsAvailable}>
      <EntityGithubActionsContent />
    </EntitySwitch.Case>
    {/*
      Here you can add support for different CI/CD services, for example
      using @backstage-community/plugin-github-actions as follows:
      <EntitySwitch.Case if={isGithubActionsAvailable}>
        <EntityGithubActionsContent />
      </EntitySwitch.Case>
     */}

    <EntitySwitch.Case>
      <EmptyState
        title="No CI/CD available for this entity"
        missing="info"
        description="You need to add an annotation to your component if you want to enable CI/CD for it. You can read more about annotations in Backstage by clicking the button below."
        action={
          <Button
            variant="contained"
            color="primary"
            href="https://backstage.io/docs/features/software-catalog/well-known-annotations"
          >
            Read more
          </Button>
        }
      />
    </EntitySwitch.Case>
  </EntitySwitch>
);

/**
 * Entity warnings component - displays orphan, relation, and processing errors.
 * Reusable across all entity kinds.
 */
const entityWarningContent = (
  <>
    <EntitySwitch>
      <EntitySwitch.Case if={isOrphan}>
        <Grid item xs={12}>
          <EntityOrphanWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasRelationWarnings}>
        <Grid item xs={12}>
          <EntityRelationWarning />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>

    <EntitySwitch>
      <EntitySwitch.Case if={hasCatalogProcessingErrors}>
        <Grid item xs={12}>
          <EntityProcessingErrorsPanel />
        </Grid>
      </EntitySwitch.Case>
    </EntitySwitch>
  </>
);

// ============================================================================
// REUSABLE TAB CONTENT BUILDERS
// ============================================================================
// These functions create tab content that can be composed into entity pages.
// This pattern makes it easy to mix and match tabs for custom entity kinds.

/**
 * Creates a standard overview tab with About card, Graph, Links, and optional subcomponents.
 * @param options.showSubcomponents - Whether to show the subcomponents card (default: true)
 * @param options.showGraph - Whether to show the catalog graph/relations card (default: true)
 */
const createOverviewTab = (options?: {
  showSubcomponents?: boolean;
  showGraph?: boolean;
}) => {
  const showSubcomponents = options?.showSubcomponents ?? true;
  const showGraph = options?.showGraph ?? true;

  return (
    <Grid container spacing={3} alignItems="stretch">
      {entityWarningContent}
      <Grid item md={showGraph ? 6 : 12}>
        <NestleAboutCardPage variant="gridItem" />
      </Grid>
      {showGraph && (
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
      )}

      <EntitySwitch>
          <EntitySwitch.Case if={isSonarQubeAvailable}>
            <Grid item md={6}>
              <EntitySonarQubeCard variant="gridItem" />
            </Grid>
        </EntitySwitch.Case>
      </EntitySwitch>

      <EntitySwitch>
        <EntitySwitch.Case if={e => Boolean(isGithubCodespacesAvailable(e))}>
          <Grid item md={6} xs={12}>
            <EntityGithubCodespacesCard enableStart={true} />
          </Grid>
        </EntitySwitch.Case>
      </EntitySwitch>

      <Grid item md={4} xs={12}>
        <EntityLinksCard />
      </Grid>
      {showSubcomponents && (
        <Grid item md={8} xs={12}>
          <EntityHasSubcomponentsCard />
        </Grid>
      )}
    </Grid>
  );
};

/**
 * Creates a dependencies tab showing component and resource dependencies.
 */
const createDependenciesTab = () => (
  <Grid container spacing={3} alignItems="stretch">
    <Grid item md={6}>
      <EntityDependsOnComponentsCard />
    </Grid>
    <Grid item md={6}>
      <EntityDependsOnResourcesCard />
    </Grid>
  </Grid>
);

/**
 * Creates an API tab showing provided and consumed APIs.
 */
const createApiTab = () => (
  <Grid container spacing={3} alignItems="stretch">
    <Grid item md={6}>
      <EntityProvidedApisCard />
    </Grid>
    <Grid item md={6}>
      <EntityConsumedApisCard />
    </Grid>
  </Grid>
);

// Backwards compatibility - keep the old constant for existing code
const overviewContent = createOverviewTab();

// ============================================================================
// ENTITY PAGE BUILDER HELPER
// ============================================================================
/**
 * Configuration interface for creating custom entity pages.
 * Use this to easily compose entity pages with only the tabs you need.
 *
 * @example
 * ```tsx
 * const myCustomPage = createEntityPage({
 *   tabs: ['overview', 'cicd', 'kubernetes', 'docs']
 * });
 * ```
 */
interface EntityPageConfig {
  /**
   * Array of tab names to include in the entity page.
   * Available tabs: 'overview', 'cicd', 'kubernetes', 'api', 'dependencies', 'docs', 'quay', 'ansible', 'secrets'
   */
  tabs: Array<'overview' | 'cicd' | 'kubernetes' | 'api' | 'dependencies' | 'docs' | 'quay' | 'secrets' | 'pull-requests' | 'azure-sites' | 'ansible'>;
  
  /**
   * Options for the overview tab
   */
  overviewOptions?: {
    /** Show the subcomponents card (default: true) */
    showSubcomponents?: boolean;
    /** Show the catalog graph/relations card (default: true) */
    showGraph?: boolean;
  };
}

/**
 * Factory function to create entity pages with specified tabs.
 * This makes it easy to create custom entity kinds with only the tabs you need.
 *
 * @param config - Configuration specifying which tabs to include
 * @returns A complete EntityLayout with the specified tabs
 *
 * @example
 * ```tsx
 * // Create a custom entity page with only overview, CI/CD, and Kubernetes tabs
 * const factorySitePage = createEntityPage({
 *   tabs: ['overview', 'cicd', 'kubernetes', 'docs']
 * });
 * ```
 */
const createEntityPage = (config: EntityPageConfig) => {
  const { tabs, overviewOptions } = config;

  return (
    <EntityLayout>
      {tabs.includes('overview') && (
        <EntityLayout.Route path="/" title="Overview">
          {createOverviewTab(overviewOptions)}
        </EntityLayout.Route>
      )}

      {tabs.includes('cicd') && (
        <EntityLayout.Route path="/ci-cd" title="CI/CD">
          {cicdContent}
        </EntityLayout.Route>
      )}

      {tabs.includes('kubernetes') && (
        <EntityLayout.Route
          path="/kubernetes"
          title="Kubernetes"
          if={isKubernetesAvailable}
        >
          <EntityKubernetesContent />
        </EntityLayout.Route>
      )}

      {tabs.includes('api') && (
        <EntityLayout.Route path="/api" title="API">
          {createApiTab()}
        </EntityLayout.Route>
      )}

      {tabs.includes('dependencies') && (
        <EntityLayout.Route path="/dependencies" title="Dependencies">
          {createDependenciesTab()}
        </EntityLayout.Route>
      )}

      {tabs.includes('docs') && (
        <EntityLayout.Route path="/docs" title="Docs">
          {techdocsContent}
        </EntityLayout.Route>
      )}

      {tabs.includes('azure-sites') && (
       <EntityLayout.Route //if={e => Boolean(isAzureWebSiteNameAvailable(e))}
         path="/azure-sites"
         title="Azure Sites">
         <EntityAzureSitesOverviewWidget />
       </EntityLayout.Route>
      )}
	  
	   {tabs.includes('pull-requests') && (
        <EntityLayout.Route
          path="/pull-requests"
          title="Pull Requests"
    // if={isGithubPullRequestsAvailable} // optional
        >
          {pullRequestsContent}
        </EntityLayout.Route>
      )}

      {tabs.includes('quay') && (
        <EntityLayout.Route if={isQuayAvailable} path="/quay" title="Quay">
          <QuayPage />
        </EntityLayout.Route>
      )}

      {tabs.includes('ansible') && (
        <EntityLayout.Route if={isAnsibleEntity} path="/ansible" title="Ansible">
          <Grid container spacing={3} alignItems="stretch">
            <Grid item md={12}>
              <EntityOverviewContent />
            </Grid>
          </Grid>
        </EntityLayout.Route>
      )}

      {tabs.includes('secrets') && (
        <EntityLayout.Route path="/secrets" title="Secrets">
          <EntityGithubSecretsContent />
        </EntityLayout.Route>
      )}
    </EntityLayout>
  );
};

// ============================================================================
// BUILT-IN ENTITY PAGES
// ============================================================================

const serviceEntityPage = createEntityPage({
  tabs: ['overview', 'cicd', 'kubernetes', 'api', 'dependencies', 'docs', 'quay', 'pull-requests', 'azure-sites', 'ansible'],
});

const websiteEntityPage = createEntityPage({
  tabs: ['overview', 'cicd', 'kubernetes', 'dependencies', 'pull-requests', 'azure-sites', 'docs'],
});

/**
 * NOTE: This page is designed to work on small screens such as mobile devices.
 * This is based on Material UI Grid. If breakpoints are used, each grid item must set the `xs` prop to a column size or to `true`,
 * since this does not default. If no breakpoints are used, the items will equitably share the available space.
 * https://material-ui.com/components/grid/#basic-grid.
 */

const defaultEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {overviewContent}
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>

    {/* Azure Sites tab */}
    <EntityLayout.Route
      path="/azure-sites"
      title="Azure Sites"
      //if={e => Boolean(isAzureWebSiteNameAvailable(e))}
    >
      <EntityAzureSitesOverviewWidget />
    </EntityLayout.Route>

    {/* Pull Requests tab */}
    <EntityLayout.Route
      path="/pull-requests"
      title="Pull Requests"
      // if={isGithubPullRequestsAvailable} // Optional: show only if annotation exists
    >
      <EntityGithubPullRequestsContent />
    </EntityLayout.Route>

    {/* Usage Statistics tab */}
    <EntityLayout.Route
      path="/usage-statistics"
      title="Insights"
    >
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TemplateUsageSummaryCard />
        </Grid>

        <Grid item xs={12}>
          <TemplateMonthlyStatsCard />
        </Grid>

        <Grid item xs={12}>
          <TemplateTaskRunsCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>

    {/* Ansible tab */}
    <EntityLayout.Route
      path="/ansible"
      title="Ansible"
      if={isAnsibleEntity}
    >
      <EntityAnsibleContent />
    </EntityLayout.Route>
  </EntityLayout>
);

const componentPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>
      {serviceEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case if={isComponentType('website')}>
      {websiteEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);

const apiPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item md={6}>
          <NestleAboutCardPage />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid container item md={12}>
          <Grid item md={6}>
            <EntityProvidingComponentsCard />
          </Grid>
          <Grid item md={6}>
            <EntityConsumingComponentsCard />
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/definition" title="Definition">
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <EntityApiDefinitionCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const userPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityUserProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const groupPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3}>
        {entityWarningContent}
        <Grid item xs={12} md={6}>
          <EntityGroupProfileCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityOwnershipCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityMembersListCard />
        </Grid>
        <Grid item xs={12} md={6}>
          <EntityLinksCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

const systemPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <Box display="flex" flexDirection="column" p={3}>
            <NestleAboutCardPage variant="gridItem" />
            <EntitySwitch>
              <EntitySwitch.Case if={isRegionalSystem}>
                <Card style={{ marginTop: "12px" }}>
                  <CardHeader title="Onboarding" />
                  <CardContent>
                    <Typography variant="body2" paragraph>
                      Follow the onboarding process to get started with the IDP.
                    </Typography>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      href="/onboarding"
                    >
                      Learn More
                    </Button>
                  </CardContent>
                </Card>
              </EntitySwitch.Case>
            </EntitySwitch>
          </Box>
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>

        <Grid item md={4} xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item md={8}>
          <EntityHasComponentsCard />
        </Grid>
        <Grid item md={6}>
          <EntityHasApisCard />
        </Grid>
        <Grid item md={6}>
          <EntityHasResourcesCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/diagram" title="Diagram">
      <EntityCatalogGraphCard
        direction={Direction.TOP_BOTTOM}
        title="System Diagram"
        height={700}
        relations={[
          RELATION_PART_OF,
          RELATION_HAS_PART,
          RELATION_API_CONSUMED_BY,
          RELATION_API_PROVIDED_BY,
          RELATION_CONSUMES_API,
          RELATION_PROVIDES_API,
          RELATION_DEPENDENCY_OF,
          RELATION_DEPENDS_ON,
        ]}
        unidirectional={false}
      />
    </EntityLayout.Route>
    <EntityLayout.Route path="/sonarqube" title="Code Quality">
        <SonarQubeRelatedEntitiesOverview relationType={RELATION_HAS_PART} entityKind="component" />
    </EntityLayout.Route>
    <EntityLayout.Route
      path="/azure-sites"
      title="Azure Sites"
      //if={e => Boolean(isAzureWebSiteNameAvailable(e))}
    >
      <EntityAzureSitesOverviewWidget />
    </EntityLayout.Route>
    <EntityLayout.Route path="/secrets" title="Secrets" if={isSecretsManagementEntity}>
      <EntityGithubSecretsContent />
    </EntityLayout.Route>
    <EntityLayout.Route path="/nexus" title="Nexus" if={isFactorySystem}>
      <EntityFactoryComponentsContent />
    </EntityLayout.Route>
  </EntityLayout>
);

const domainPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <Grid container spacing={3} alignItems="stretch">
        {entityWarningContent}
        <Grid item md={6}>
          <NestleAboutCardPage variant="gridItem" />
        </Grid>
        <Grid item md={6} xs={12}>
          <EntityCatalogGraphCard height={400} />
        </Grid>
        <Grid item md={6}>
          <EntityHasSystemsCard />
        </Grid>
      </Grid>
    </EntityLayout.Route>
  </EntityLayout>
);

// ============================================================================
// CUSTOM ENTITY PAGES
// ============================================================================
// Add your custom entity kinds here using the createEntityPage helper.
// This section is designed for easy maintenance and extension.

const resourcePage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      {createOverviewTab({ showSubcomponents: false })}
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/data-storage"
      title="Data Storage"
      if={isResourceType('storage-account')}
    >
      <StorageAccountContent />
    </EntityLayout.Route>

    <EntityLayout.Route
      path="/secrets"
      title="Secrets"
      if={isResourceType('key-vault')}
    >
      <ManagementConfigsContent title="KeyVault Secrets List" />
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

// ============================================================================
// MAIN ENTITY PAGE ROUTER
// ============================================================================

export const entityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isKind('component')} children={componentPage} />
    <EntitySwitch.Case if={isKind('api')} children={apiPage} />
    <EntitySwitch.Case if={isKind('group')} children={groupPage} />
    <EntitySwitch.Case if={isKind('user')} children={userPage} />
    <EntitySwitch.Case if={isKind('system')} children={systemPage} />
    <EntitySwitch.Case if={isKind('domain')} children={domainPage} />
    <EntitySwitch.Case if={isKind('resource')} children={resourcePage} />

    <EntitySwitch.Case>{defaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
